import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} container - The main drawer container.
 * @property {HTMLButtonElement} trigger - The button that opens and closes the main drawer.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['container', 'trigger'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.container.hasAttribute('data-open');
  }

  /**
   * Get the closest submenu details element to the event target, or the main drawer container
   * @param {Event | undefined} event
   * @returns {HTMLElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.container;

    return event.target.closest('details') ?? this.refs.container;
  }

  /**
   * Set the main drawer open state. The trigger is a <button>, so this replaces what <details>/<summary>
   * did natively: the `open` state, the page scroll lock and the `toggle` event.
   * @param {boolean} open
   */
  #setMainOpen(open) {
    const { container, trigger } = this.refs;

    container.toggleAttribute('data-open', open);
    trigger.setAttribute('aria-label', (open ? trigger.dataset.labelClose : trigger.dataset.labelOpen) ?? '');
    document.documentElement.toggleAttribute('scroll-lock', open);
    container.dispatchEvent(new CustomEvent('header-drawer:toggle', { detail: { open } }));
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);

    if (details === this.refs.container) {
      // No aria-expanded on the trigger: it opens a modal dialog, the Menu/Close label carries the state.
      this.#setMainOpen(true);
    } else if (!details.querySelector('summary')) {
      return;
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.container);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLElement} details
   */
  #close(details) {
    const isMain = details === this.refs.container;

    if (isMain ? !this.isOpen : !details.querySelector('summary')) return;

    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      if (isMain) {
        this.#setMainOpen(false);
        removeTrapFocus();
        this.refs.trigger.focus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        reset(details);
        setTimeout(() => {
          trapFocus(this.refs.container);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
}
